import type { ItemsReport, OperationsReport, ReportMetrics, RevenueTrendPoint } from '@/types/api';

export interface ReportExportMeta {
  restaurantName: string;
  startDate: string;
  endDate: string;
  /** Currency formatter bound to the selected restaurant. */
  formatCurrency: (value: number | string) => string;
}

// ──────────────────────────── CSV primitives ────────────────────────────

/** Escape a single CSV cell per RFC 4180 (quote when it contains "," | '"' | newline). */
function csvCell(value: unknown): string {
  const s = value == null ? '' : String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function csvRow(cells: unknown[]): string {
  return cells.map(csvCell).join(',');
}

const mins = (m: number) => `${Number(m).toFixed(0)}m`;
const pct = (v: number) => `${(Number(v) * 100).toFixed(0)}%`;

/** Trigger a client-side file download of text content. */
export function downloadTextFile(filename: string, content: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

export function downloadCsv(filename: string, content: string): void {
  downloadTextFile(filename, content, 'text/csv;charset=utf-8;');
}

// ──────────────────────────── PDF (print window) ────────────────────────────

function esc(value: unknown): string {
  const s = value == null ? '' : String(value);
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Render an HTML table; first column is left-aligned, the rest follow `td.r`. */
function htmlTable(headers: string[], rows: (string | number)[][]): string {
  const thead = `<tr>${headers.map((h) => `<th>${esc(h)}</th>`).join('')}</tr>`;
  const tbody = rows
    .map((r) => `<tr>${r.map((c, i) => `<td class="${i === 0 ? 'l' : 'r'}">${esc(c)}</td>`).join('')}</tr>`)
    .join('');
  return `<table><thead>${thead}</thead><tbody>${tbody}</tbody></table>`;
}

interface PrintWindowOptions {
  title: string;
  meta: ReportExportMeta;
  kpis: [string, string][];
  /** Pre-rendered section HTML (use `section()` to build each block). */
  bodyHtml: string;
}

/** Build one "<h2> + table" section. */
function section(heading: string, headers: string[], rows: (string | number)[][]): string {
  return `<h2>${esc(heading)}</h2>${htmlTable(headers, rows)}`;
}

/**
 * Open a print-ready window with only the report content and invoke the browser
 * print dialog, where the user can choose "Save as PDF". Zero-dependency PDF export.
 * Falls back to printing the current page if pop-ups are blocked.
 */
function openPrintWindow(opts: PrintWindowOptions): void {
  const win = window.open('', '_blank', 'width=900,height=1000');
  if (!win) {
    window.print();
    return;
  }

  const body = `
    <h1>${esc(opts.title)}</h1>
    <p class="meta">${esc(opts.meta.restaurantName)} &middot; ${esc(opts.meta.startDate)} to ${esc(opts.meta.endDate)}</p>
    <div class="kpis">
      ${opts.kpis
        .map(([l, v]) => `<div class="kpi"><div class="kv">${esc(v)}</div><div class="kl">${esc(l)}</div></div>`)
        .join('')}
    </div>
    ${opts.bodyHtml}
  `;

  win.document.write(`<!doctype html><html><head><meta charset="utf-8" />
    <title>${esc(opts.title)} — ${esc(opts.meta.restaurantName)}</title>
    <style>
      * { box-sizing: border-box; }
      body { font-family: -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; color: #111827; margin: 32px; }
      h1 { font-size: 22px; margin: 0 0 4px; }
      h2 { font-size: 15px; margin: 24px 0 8px; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; }
      .meta { color: #6b7280; margin: 0 0 16px; font-size: 13px; }
      .kpis { display: flex; flex-wrap: wrap; gap: 12px; }
      .kpi { border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px 14px; min-width: 120px; }
      .kv { font-size: 18px; font-weight: 700; }
      .kl { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: .03em; }
      table { width: 100%; border-collapse: collapse; font-size: 12px; }
      th, td { padding: 6px 8px; border-bottom: 1px solid #f0f0f0; }
      th { text-align: left; background: #f9fafb; font-size: 11px; text-transform: uppercase; color: #6b7280; }
      td.r { text-align: left; }
      @media print { body { margin: 0; } h2 { page-break-after: avoid; } tr { page-break-inside: avoid; } }
    </style></head><body>${body}</body></html>`);
  win.document.close();
  win.focus();
  // Give the new document a tick to lay out before printing.
  win.setTimeout(() => win.print(), 250);
}

// ──────────────────────────── Operations report ────────────────────────────

export function buildOperationsCsv(report: OperationsReport, meta: ReportExportMeta): string {
  const { formatCurrency } = meta;
  const lines: string[] = [];

  lines.push(csvRow(['Operations Report']));
  lines.push(csvRow(['Restaurant', meta.restaurantName]));
  lines.push(csvRow(['Period', `${meta.startDate} to ${meta.endDate}`]));
  lines.push('');

  lines.push(csvRow(['Summary']));
  lines.push(csvRow(['Metric', 'Value']));
  lines.push(csvRow(['Table Turns', report.totalTurns]));
  lines.push(csvRow(['Covers', report.totalCovers]));
  lines.push(csvRow(['Avg Covers / Turn', Number(report.avgCoversPerTurn).toFixed(2)]));
  lines.push(csvRow(['Avg Session', mins(report.avgSessionMinutes)]));
  lines.push(csvRow(['Median Session', mins(report.medianSessionMinutes)]));
  lines.push(csvRow(['Seat Occupancy', pct(report.seatOccupancyRate)]));
  lines.push(csvRow(['RevPASH', formatCurrency(report.revPASH)]));
  lines.push(csvRow(['Spend / Cover', formatCurrency(report.spendPerCover)]));
  lines.push(csvRow(['Spend / Turn', formatCurrency(report.spendPerTurn)]));
  lines.push(csvRow(['Total Seats', report.totalSeats]));
  lines.push('');

  lines.push(csvRow(['Peak Hours']));
  lines.push(csvRow(['Hour', 'Turns', 'Covers']));
  report.peakHours.forEach((h) => lines.push(csvRow([h.hour, h.turns, h.covers])));
  lines.push('');

  lines.push(csvRow(['Session Length Distribution']));
  lines.push(csvRow(['Range', 'Sessions']));
  report.sessionLengthHistogram.forEach((s) => lines.push(csvRow([s.label, s.count])));
  lines.push('');

  lines.push(csvRow(['Table Turns']));
  lines.push(csvRow(['Table', 'Floor', 'Turns', 'Covers', 'Avg Session', 'Revenue']));
  report.tableTurns.forEach((t) =>
    lines.push(
      csvRow([t.tableName || t.tableId, t.floorName ?? '', t.turns, t.covers, mins(t.avgSessionMinutes), formatCurrency(t.revenue)]),
    ),
  );
  lines.push('');

  lines.push(csvRow(['Server Performance']));
  lines.push(csvRow(['Server', 'Turns', 'Covers', 'Avg Session', 'Revenue']));
  report.serverPerformance.forEach((s) =>
    lines.push(csvRow([s.serverName, s.turns, s.covers, mins(s.avgSessionMinutes), formatCurrency(s.revenue)])),
  );
  lines.push('');

  lines.push(csvRow(['Booking Operations']));
  lines.push(csvRow(['Metric', 'Value']));
  lines.push(csvRow(['Bookings', report.bookingOps.totalBookings]));
  lines.push(csvRow(['No-shows', report.bookingOps.noShows]));
  lines.push(csvRow(['No-show Rate', pct(report.bookingOps.noShowRate)]));
  lines.push(csvRow(['Avg Party Size', Number(report.bookingOps.avgPartySize).toFixed(1)]));

  return lines.join('\r\n');
}

export function printOperationsReport(report: OperationsReport, meta: ReportExportMeta): void {
  const { formatCurrency } = meta;
  openPrintWindow({
    title: 'Operations Report',
    meta,
    kpis: [
      ['Table Turns', Number(report.totalTurns).toLocaleString()],
      ['Covers', Number(report.totalCovers).toLocaleString()],
      ['Avg Session', mins(report.avgSessionMinutes)],
      ['Seat Occupancy', pct(report.seatOccupancyRate)],
      ['RevPASH', formatCurrency(report.revPASH)],
      ['Spend / Cover', formatCurrency(report.spendPerCover)],
    ],
    bodyHtml: [
      section('Peak Hours', ['Hour', 'Turns', 'Covers'], report.peakHours.map((h) => [h.hour, h.turns, h.covers])),
      section(
        'Table Turns',
        ['Table', 'Floor', 'Turns', 'Covers', 'Avg Session', 'Revenue'],
        report.tableTurns.map((t) => [
          t.tableName || t.tableId,
          t.floorName ?? '—',
          t.turns,
          t.covers,
          mins(t.avgSessionMinutes),
          formatCurrency(t.revenue),
        ]),
      ),
      section(
        'Server Performance',
        ['Server', 'Turns', 'Covers', 'Avg Session', 'Revenue'],
        report.serverPerformance.map((s) => [s.serverName, s.turns, s.covers, mins(s.avgSessionMinutes), formatCurrency(s.revenue)]),
      ),
      section(
        'Booking Operations',
        ['Bookings', 'No-shows', 'No-show Rate', 'Avg Party'],
        [[
          report.bookingOps.totalBookings,
          report.bookingOps.noShows,
          pct(report.bookingOps.noShowRate),
          Number(report.bookingOps.avgPartySize).toFixed(1),
        ]],
      ),
    ].join(''),
  });
}

// ──────────────────────────── Sales report ────────────────────────────

export function buildSalesCsv(
  revenue: ReportMetrics,
  trend: RevenueTrendPoint[],
  orderTypeMix: OperationsReport['orderTypeMix'] | undefined,
  meta: ReportExportMeta,
): string {
  const { formatCurrency } = meta;
  const lines: string[] = [];

  lines.push(csvRow(['Sales Report']));
  lines.push(csvRow(['Restaurant', meta.restaurantName]));
  lines.push(csvRow(['Period', `${meta.startDate} to ${meta.endDate}`]));
  lines.push('');

  lines.push(csvRow(['Summary']));
  lines.push(csvRow(['Metric', 'Value']));
  lines.push(csvRow(['Total Revenue', formatCurrency(revenue.revenue)]));
  lines.push(csvRow(['Total Orders', revenue.orders]));
  lines.push(csvRow(['Avg Order Value', formatCurrency(revenue.averageOrderValue)]));
  lines.push('');

  lines.push(csvRow(['Revenue Trend']));
  lines.push(csvRow(['Date', 'Revenue', 'Orders']));
  trend.forEach((p) => lines.push(csvRow([p.date, formatCurrency(p.revenue), p.orders])));
  lines.push('');

  if (orderTypeMix && orderTypeMix.length) {
    lines.push(csvRow(['Revenue by Order Type']));
    lines.push(csvRow(['Order Type', 'Tickets', 'Revenue', 'Share']));
    orderTypeMix.forEach((o) =>
      lines.push(csvRow([o.orderType, o.tickets, formatCurrency(o.revenue), pct(o.sharePct / 100)])),
    );
    lines.push('');
  }

  lines.push(csvRow(['Revenue by Hour']));
  lines.push(csvRow(['Hour', 'Revenue']));
  revenue.revenueByHour.forEach((h) => lines.push(csvRow([h.hour, formatCurrency(h.revenue)])));
  lines.push('');

  lines.push(csvRow(['Revenue by Category']));
  lines.push(csvRow(['Category', 'Revenue']));
  revenue.revenueByCategory.forEach((c) => lines.push(csvRow([c.category, formatCurrency(c.revenue)])));
  lines.push('');

  lines.push(csvRow(['Payment Method Breakdown']));
  lines.push(csvRow(['Method', 'Count', 'Amount']));
  revenue.paymentMethodBreakdown.forEach((p) => lines.push(csvRow([p.method, p.count, formatCurrency(p.amount)])));
  lines.push('');

  lines.push(csvRow(['Top Selling Items']));
  lines.push(csvRow(['Item', 'Quantity', 'Revenue']));
  revenue.topSellingItems.forEach((i) => lines.push(csvRow([i.name, i.quantity, formatCurrency(i.revenue)])));

  return lines.join('\r\n');
}

export function printSalesReport(
  revenue: ReportMetrics,
  trend: RevenueTrendPoint[],
  orderTypeMix: OperationsReport['orderTypeMix'] | undefined,
  meta: ReportExportMeta,
): void {
  const { formatCurrency } = meta;
  const sections = [
    section('Revenue Trend', ['Date', 'Revenue', 'Orders'], trend.map((p) => [p.date, formatCurrency(p.revenue), p.orders])),
  ];
  if (orderTypeMix && orderTypeMix.length) {
    sections.push(
      section(
        'Revenue by Order Type',
        ['Order Type', 'Tickets', 'Revenue', 'Share'],
        orderTypeMix.map((o) => [o.orderType, o.tickets, formatCurrency(o.revenue), `${Number(o.sharePct).toFixed(0)}%`]),
      ),
    );
  }
  sections.push(
    section('Revenue by Hour', ['Hour', 'Revenue'], revenue.revenueByHour.map((h) => [h.hour, formatCurrency(h.revenue)])),
    section('Revenue by Category', ['Category', 'Revenue'], revenue.revenueByCategory.map((c) => [c.category, formatCurrency(c.revenue)])),
    section(
      'Payment Method Breakdown',
      ['Method', 'Count', 'Amount'],
      revenue.paymentMethodBreakdown.map((p) => [p.method, p.count, formatCurrency(p.amount)]),
    ),
    section('Top Selling Items', ['Item', 'Quantity', 'Revenue'], revenue.topSellingItems.map((i) => [i.name, i.quantity, formatCurrency(i.revenue)])),
  );

  openPrintWindow({
    title: 'Sales Report',
    meta,
    kpis: [
      ['Total Revenue', formatCurrency(revenue.revenue)],
      ['Total Orders', Number(revenue.orders).toLocaleString()],
      ['Avg Order Value', formatCurrency(revenue.averageOrderValue)],
    ],
    bodyHtml: sections.join(''),
  });
}

// ──────────────────────────── Menu (items) report ────────────────────────────

export function buildMenuCsv(report: ItemsReport, meta: ReportExportMeta): string {
  const { formatCurrency } = meta;
  const lines: string[] = [];

  lines.push(csvRow(['Menu Insights Report']));
  lines.push(csvRow(['Restaurant', meta.restaurantName]));
  lines.push(csvRow(['Period', `${meta.startDate} to ${meta.endDate}`]));
  lines.push('');

  lines.push(csvRow(['Summary']));
  lines.push(csvRow(['Metric', 'Value']));
  lines.push(csvRow(['Total Items Sold', report.totalItemsSold]));
  lines.push(csvRow(['Total Items Revenue', formatCurrency(report.totalItemsRevenue)]));
  lines.push(csvRow(['Distinct Items', report.distinctItemsCount]));
  lines.push(csvRow(['Avg Item Price', formatCurrency(report.averageItemPrice)]));
  lines.push('');

  lines.push(csvRow(['Revenue by Category']));
  lines.push(csvRow(['Category', 'Quantity', 'Revenue']));
  report.revenueByCategory.forEach((c) => lines.push(csvRow([c.category, c.quantity, formatCurrency(c.revenue)])));
  lines.push('');

  lines.push(csvRow(['Items Sold by Hour']));
  lines.push(csvRow(['Hour', 'Quantity', 'Revenue']));
  report.itemsSoldByHour.forEach((h) => lines.push(csvRow([h.hour, h.quantity, formatCurrency(h.revenue)])));
  lines.push('');

  lines.push(csvRow(['Top Selling Items']));
  lines.push(csvRow(['Item', 'Quantity', 'Revenue']));
  report.topSellingItems.forEach((i) => {
    lines.push(csvRow([i.name, i.quantity, formatCurrency(i.revenue)]));
    (i.subitems ?? []).forEach((s) => lines.push(csvRow([`  ↳ ${s.name}`, s.quantity, formatCurrency(s.revenue)])));
  });

  return lines.join('\r\n');
}

export function printMenuReport(report: ItemsReport, meta: ReportExportMeta): void {
  const { formatCurrency } = meta;
  const itemRows: (string | number)[][] = [];
  report.topSellingItems.forEach((i) => {
    itemRows.push([i.name, i.quantity, formatCurrency(i.revenue)]);
    (i.subitems ?? []).forEach((s) => itemRows.push([`↳ ${s.name}`, s.quantity, formatCurrency(s.revenue)]));
  });

  openPrintWindow({
    title: 'Menu Insights Report',
    meta,
    kpis: [
      ['Total Items Sold', Number(report.totalItemsSold).toLocaleString()],
      ['Total Items Revenue', formatCurrency(report.totalItemsRevenue)],
      ['Distinct Items', Number(report.distinctItemsCount).toLocaleString()],
      ['Avg Item Price', formatCurrency(report.averageItemPrice)],
    ],
    bodyHtml: [
      section('Revenue by Category', ['Category', 'Quantity', 'Revenue'], report.revenueByCategory.map((c) => [c.category, c.quantity, formatCurrency(c.revenue)])),
      section('Items Sold by Hour', ['Hour', 'Quantity', 'Revenue'], report.itemsSoldByHour.map((h) => [h.hour, h.quantity, formatCurrency(h.revenue)])),
      section('Top Selling Items', ['Item', 'Quantity', 'Revenue'], itemRows),
    ].join(''),
  });
}

// ──────────────────────────── Generic tabular export ────────────────────────────

/** A titled table block used by the generic CSV/PDF exporters. */
export interface ReportSection {
  heading: string;
  headers: string[];
  rows: (string | number)[][];
}

/** Build a multi-section CSV document (title + KPI lines + each section's table). */
export function buildGenericCsv(
  title: string,
  meta: ReportExportMeta,
  kpis: [string, string][],
  sections: ReportSection[],
): string {
  const lines: string[] = [];
  lines.push(csvRow([title]));
  lines.push(csvRow([meta.restaurantName, `${meta.startDate} to ${meta.endDate}`]));
  lines.push('');
  if (kpis.length) {
    kpis.forEach(([l, v]) => lines.push(csvRow([l, v])));
    lines.push('');
  }
  sections.forEach((s) => {
    lines.push(csvRow([s.heading]));
    lines.push(csvRow(s.headers));
    s.rows.forEach((r) => lines.push(csvRow(r)));
    lines.push('');
  });
  return lines.join('\r\n');
}

/** Open the print/PDF window for a multi-section tabular report. */
export function printGenericReport(
  title: string,
  meta: ReportExportMeta,
  kpis: [string, string][],
  sections: ReportSection[],
): void {
  openPrintWindow({
    title,
    meta,
    kpis,
    bodyHtml: sections.map((s) => section(s.heading, s.headers, s.rows)).join(''),
  });
}
